import type { Recipe } from "./synth";

/**
 * Звуки игры — рецепты синтеза (audio/synth.ts), имена — из данных приёмов (sound.start, sound.hit) и событий мира.
 * Разбор проб (консультант и Gemini, 05.11.2026): звук должен жить и на динамиках телефона и ТВ (ниже 150 Гц они
 * почти не играют) — в ударе выше 800 Гц не меньше 15–20 % энергии. Удар: щелчок 2–5 кГц, треск 1–3 кГц, тело 120–250
 * Гц (треугольник — с призвуками), шлепок по телу 1,2 → 0,6 кГц; короткий отзвук зала. Падение: стук 1–3 кГц,
 * середина 150–500 Гц, низ. Сердце — «тук-тук» с паузой 0,15 с и призвуком 100–200 Гц.
 */
const ROOM = { count: 2, delay: 0.07, decay: 0.28 } as const;

const hit = (power: number): Recipe => [
  // щелчок атаки и треск — то, что слышно на любом динамике
  {
    kind: "noise",
    duration: 0.025,
    gain: 0.55 * power,
    attack: 0.002,
    filter: { type: "bandpass", from: 4200, to: 2200, q: 0.8 },
  },
  {
    kind: "noise",
    duration: 0.05,
    gain: 0.45 * power,
    attack: 0.002,
    filter: { type: "bandpass", from: 2400, to: 1100, q: 0.9 },
    echo: ROOM,
  },
  // тело удара 220 → 90 Гц, треугольник — с призвуками в середине
  {
    kind: "tone",
    duration: 0.05 + 0.05 * power,
    gain: 0.4 * power,
    attack: 0.002,
    pitch: { from: 220, to: 90 },
    wave: "triangle",
  },
  // шлепок по телу
  {
    kind: "noise",
    at: 0.004,
    duration: 0.06 + 0.04 * power,
    gain: 0.5 * power,
    attack: 0.002,
    filter: { type: "bandpass", from: 1200, to: 600, q: 0.9 },
    echo: ROOM,
  },
];

export const SOUNDS: Readonly<Record<string, Recipe>> = {
  "hit-light": hit(0.75),
  "hit-medium": hit(0.95),
  "hit-heavy": [
    ...hit(1.15),
    {
      kind: "tone",
      at: 0.008,
      duration: 0.16,
      gain: 0.2,
      attack: 0.003,
      pitch: { from: 200, to: 90 },
      wave: "triangle",
    },
    {
      kind: "noise",
      duration: 0.06,
      gain: 0.5,
      attack: 0.002,
      filter: { type: "bandpass", from: 3000, to: 1400, q: 0.8 },
      echo: ROOM,
    },
  ],
  block: [
    { kind: "tone", duration: 0.06, gain: 0.25, attack: 0.002, pitch: { from: 1100, to: 800 }, wave: "triangle" },
    {
      kind: "noise",
      duration: 0.07,
      gain: 0.4,
      attack: 0.002,
      filter: { type: "bandpass", from: 3500, to: 2000, q: 0.9 },
      echo: ROOM,
    },
  ],
  // свист — с выходом руки, а не с началом замаха
  whoosh: [
    {
      kind: "noise",
      at: 0.08,
      duration: 0.16,
      gain: 0.22,
      attack: 0.04,
      filter: { type: "bandpass", from: 700, to: 2600, q: 1.2 },
    },
  ],
  step: [
    {
      kind: "noise",
      duration: 0.04,
      gain: 0.16,
      attack: 0.002,
      filter: { type: "bandpass", from: 1800, to: 700, q: 0.9 },
    },
    { kind: "tone", duration: 0.05, gain: 0.1, attack: 0.004, pitch: { from: 120, to: 70 }, wave: "triangle" },
  ],
  // удар тела о пол: стук 1–3 кГц, середина 150–500 Гц, низ — и отзвук зала
  land: [
    {
      kind: "noise",
      duration: 0.06,
      gain: 0.85,
      attack: 0.002,
      filter: { type: "bandpass", from: 2600, to: 1200, q: 0.8 },
      echo: ROOM,
    },
    {
      kind: "noise",
      duration: 0.22,
      gain: 0.75,
      attack: 0.003,
      filter: { type: "bandpass", from: 520, to: 200, q: 0.8 },
      echo: ROOM,
    },
    { kind: "tone", duration: 0.18, gain: 0.3, attack: 0.003, pitch: { from: 160, to: 70 }, wave: "triangle" },
  ],
  ko: [
    {
      kind: "noise",
      duration: 0.05,
      gain: 0.5,
      attack: 0.002,
      filter: { type: "bandpass", from: 2400, to: 1000, q: 0.8 },
    },
    { kind: "tone", duration: 0.7, gain: 0.45, attack: 0.004, pitch: { from: 180, to: 60 }, wave: "triangle" },
    {
      kind: "noise",
      duration: 0.6,
      gain: 0.45,
      filter: { type: "bandpass", from: 900, to: 150, q: 0.7 },
      echo: { count: 3, delay: 0.12, decay: 0.4 },
    },
  ],
  // замедление: свип вниз 600 → 60 Гц с длинным отзвуком (зал растягивается)
  slowmo: [
    {
      kind: "tone",
      duration: 1,
      gain: 0.35,
      attack: 0.02,
      pitch: { from: 600, to: 60 },
      wave: "triangle",
      echo: { count: 4, delay: 0.18, decay: 0.5 },
    },
    {
      kind: "noise",
      duration: 0.8,
      gain: 0.3,
      attack: 0.05,
      filter: { type: "bandpass", from: 2400, to: 300, q: 1.4 },
      echo: { count: 4, delay: 0.18, decay: 0.5 },
    },
  ],
  // сердце «тук-тук»: две доли с паузой ~0,15 с, низ плюс призвук 100–200 Гц, мягкая атака
  heartbeat: [
    { kind: "tone", duration: 0.11, gain: 0.6, attack: 0.008, pitch: { from: 70, to: 55 } },
    { kind: "tone", duration: 0.1, gain: 0.3, attack: 0.006, pitch: { from: 180, to: 120 }, wave: "triangle" },
    { kind: "tone", at: 0.26, duration: 0.09, gain: 0.5, attack: 0.006, pitch: { from: 85, to: 65 } },
    {
      kind: "tone",
      at: 0.26,
      duration: 0.08,
      gain: 0.28,
      attack: 0.005,
      pitch: { from: 200, to: 140 },
      wave: "triangle",
    },
  ],
  blink: [
    { kind: "tone", duration: 0.25, gain: 0.18, attack: 0.01, pitch: { from: 520, to: 1900 } },
    { kind: "noise", duration: 0.22, gain: 0.16, attack: 0.03, filter: { type: "highpass", from: 2400, to: 6000 } },
  ],
  vanish: [
    { kind: "tone", duration: 0.4, gain: 0.18, attack: 0.01, pitch: { from: 1500, to: 300 } },
    {
      kind: "noise",
      duration: 0.35,
      gain: 0.16,
      attack: 0.05,
      filter: { type: "bandpass", from: 5000, to: 900, q: 1 },
    },
  ],
  super: [
    { kind: "tone", duration: 0.8, gain: 0.3, attack: 0.2, pitch: { from: 80, to: 420 }, wave: "sawtooth" },
    {
      kind: "noise",
      duration: 0.8,
      gain: 0.25,
      attack: 0.3,
      filter: { type: "bandpass", from: 300, to: 2400, q: 0.9 },
    },
  ],
};
